import React, { useEffect, useState } from "react";
import axios from "axios";
import { LoaderType, Loading } from "@/layouts/buddy/loading";
import { KeenIcon } from "@/components";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useStatistic } from "@/contexts/StatisticContext.tsx";

// Unified interface to represent any entity.
interface Entity {
    p_id: string;
    name: string;
}

interface CrawlWebpageProps {
    /**
     * The parent entity this crawl relates to (candidate, job, or company), if any.
     * For job crawls in create mode, parentEntity is required.
     */
    parentEntity?: Entity | null;
    /**
     * The object entity this crawl targets (the actual entity to which the URL source is added).
     * For addSource mode, this becomes essential.
     */
    objectEntity?: Entity | null;
    /** The type of entity this crawl relates to: "candidate", "job", or "company". */
    entityType: "candidate" | "job" | "company";
    /**
     * The mode affects behavior or endpoint selection:
     *   - "create"    for creating a new entity,
     *   - "addSource" for adding additional info (source) to an existing entity.
     */
    mode: "create" | "addSource";
    /** Optional callback to trigger when the crawl succeeds and data needs to be refreshed. */
    onSuccess?: () => void;
}

const CrawlWebpage: React.FC<CrawlWebpageProps> = ({
                                                       parentEntity = null,
                                                       objectEntity = null,
                                                       entityType,
                                                       mode,
                                                       onSuccess,
                                                   }) => {
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState<string>("");
    const navigate = useNavigate();
    const { refreshStatistic } = useStatistic();

    // Map for webpage-crawl endpoints.
    const apiUrlMap = {
        candidate: {
            create: `${import.meta.env.VITE_APP_API_URL}/candidate/create/url`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/candidate/add-source/url`,
        },
        job: {
            create: `${import.meta.env.VITE_APP_API_URL}/job/create/url`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/job/add-source/url`,
        },
        company: {
            create: `${import.meta.env.VITE_APP_API_URL}/company/create/url`,
            addSource: `${import.meta.env.VITE_APP_API_URL}/company/add-source/url`,
        },
    };

    const API_WEBPAGE_CRAWL = apiUrlMap[entityType][mode];

    // Determine the final crawl URL.
    // For create mode, if a parentEntity is provided (and entity isn't "company"), append its p_id.
    // For addSource mode, if an objectEntity is provided (and entity isn't "company"), append its p_id.
    let crawlUrl = API_WEBPAGE_CRAWL;
    if (mode === "create" && parentEntity?.p_id && entityType !== "company") {
        crawlUrl = `${API_WEBPAGE_CRAWL}/${parentEntity.p_id}`;
    } else if (mode === "addSource" && objectEntity?.p_id) {
        crawlUrl = `${API_WEBPAGE_CRAWL}/${objectEntity.p_id}`;
    }

    // Warn if a required entity is missing.
    useEffect(() => {
        if (mode === "create" && entityType === "job" && !parentEntity?.p_id) {
            enqueueSnackbar("Please select the parent company.", {
                variant: "solid",
                state: "danger",
            });
        }
        if (mode === "addSource" && !objectEntity?.p_id) {
            enqueueSnackbar("Please select the object entity.", {
                variant: "solid",
                state: "danger",
            });
        }
    }, [parentEntity, objectEntity, entityType, mode]);

    // URL validation: prepend "https://" if missing.
    const isValidUrl = (urlString: string) => {
        let formatted = urlString;
        if (!formatted.startsWith("http://") && !formatted.startsWith("https://")) {
            formatted = "https://" + formatted;
        }
        try {
            new URL(formatted);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = async () => {
        // For job creation, ensure a valid parent is provided.
        if (mode === "create" && entityType === "job" && !parentEntity?.p_id) {
            enqueueSnackbar("Please select the parent company before submitting a URL.", {
                variant: "solid",
                state: "danger",
            });
            return;
        }

        // Prepend "https://" if missing.
        let formattedUrl = url;
        if (!formattedUrl.startsWith("http://") && !formattedUrl.startsWith("https://")) {
            formattedUrl = "https://" + formattedUrl;
            setUrl(formattedUrl);
        }

        // Validate the URL.
        if (!isValidUrl(formattedUrl)) {
            enqueueSnackbar("Please provide a valid URL.", {
                variant: "solid",
                state: "danger",
            });
            return;
        }

        setLoading(true);

        try {
            // POST the URL.
            const response = await axios.post(
                crawlUrl,
                { url: formattedUrl },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            // Construct the path to the newly created or updated entity.
            const newPath = `/${entityType}/${response.data.p_id}`;

            // If we’re already on that path, just refresh; otherwise, navigate.
            if (window.location.pathname === newPath) {
                onSuccess?.();
            } else {
                navigate(newPath);
            }

            // Clear the URL field.
            setUrl("");

            // Refresh statistics.
            await refreshStatistic();

            // Build and display success message.
            const successMessages: Record<string, string> = {
                candidate:
                    mode === "create"
                        ? "Candidate has been successfully created."
                        : "Candidate source has been successfully added.",
                job:
                    mode === "create"
                        ? "Job has been successfully created."
                        : "Job source has been successfully added.",
                company:
                    mode === "create"
                        ? "Company has been successfully created."
                        : "Company source has been successfully added.",
            };

            enqueueSnackbar(successMessages[entityType], {
                variant: "solid",
                state: "success",
            });
        } catch (err) {
            enqueueSnackbar(`Error: ${err}`, {
                variant: "solid",
                state: "danger",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card pb-2.5">
            <div className="card-header" id="crawl_webpage">
                <h3 className="card-title">Webpage</h3>
            </div>
            <div className="card-body grid gap-5">
                {!loading && (
                    <div className="flex flex-col gap-4">
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="Enter the URL of the webpage to crawl."
                            className="border border-gray-400 p-3 rounded-md text-sm w-full focus:outline-none"
                        />
                        <div className="flex justify-end pt-2.5">
                            <button
                                onClick={handleSubmit}
                                className="btn btn-sm btn-primary"
                                disabled={!url || !isValidUrl(url)}
                            >
                                <KeenIcon icon="plus" /> Submit Webpage
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <Loading
                        loadingText="Processing Webpage"
                        loaderType={LoaderType.LOADING_FILES}
                        loading={loading}
                        delay={0}
                    />
                )}
            </div>
        </div>
    );
};

export { CrawlWebpage };
